import * as ProductService from "@omni/services/product/product.service";
import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import {
    AemEnergyLabelRenditions,
    EnergyLabel
} from "@omni/types/components/product/energyLabel/energyLabel.type";
import { ModelMapper } from "@omni/types/utils/modelMapper";

// Legacy - Feature containing the Eprel ID
const GPC_EPREL_FEATURE_CODE_SUFFIX = "9.147";
// Legacy - Before introduction of Eprel ID
const GPC_ENERGY_FEATURE_CODE_SUFFIX = "4.242";

const ONE_BY_TWO_XS_RENDITION = "250x500";
const ONE_BY_TWO_SM_RENDITION = "350x700";
const ONE_BY_TWO_MD_RENDITION = "480x960";
const ONE_BY_TWO_LG_RENDITION = "600x1200";

const HYBRIS_THUMBNAIL_RENDITION = "110x82";
const HYBRIS_MAIN_RENDITION = "440x330";
const HYBRIS_ZOOM_RENDITION = "800x600";
const HYBRIS_MAX_ZOOM_RENDITION = "1920x1440";

const fromFeature: ModelMapper<
    Product<ProductFieldSet.Full | ProductFieldSet.ProductTile>,
    EnergyLabel | undefined
> = (product) => {
    const eprelFeatureValue = ProductService.getFeatureValue(
        product,
        GPC_EPREL_FEATURE_CODE_SUFFIX
    );

    const energyFeatureValue = ProductService.getFeatureValue(
        product,
        GPC_ENERGY_FEATURE_CODE_SUFFIX
    );

    if (!eprelFeatureValue && !energyFeatureValue) return;

    const energyLabelLarge =
        "energyLabels" in product
            ? product.energyLabels.at(0)?.medias.reduce(
                  (acc, curr) => {
                      if (curr.altText) {
                          acc.altText = curr.altText;
                      }
                      switch (curr.format) {
                          case ONE_BY_TWO_XS_RENDITION:
                          case HYBRIS_THUMBNAIL_RENDITION:
                              acc.thumbnail = curr.url;
                              break;
                          case ONE_BY_TWO_SM_RENDITION:
                          case HYBRIS_MAIN_RENDITION:
                              acc.main = curr.url;
                              break;
                          case ONE_BY_TWO_MD_RENDITION:
                          case HYBRIS_ZOOM_RENDITION:
                              acc.zoom = curr.url;
                              break;
                          case ONE_BY_TWO_LG_RENDITION:
                          case HYBRIS_MAX_ZOOM_RENDITION:
                              acc.maxZoom = curr.url;
                              break;
                      }
                      return acc;
                  },
                  { altText: product.productTitle } as AemEnergyLabelRenditions
              )
            : null;

    return {
        eprelId: eprelFeatureValue?.formattedValue || null,
        // Legacy fallback for products that don't have the eprel feature but do have the old energy feature
        label: energyLabelLarge || null,
        // Legacy fallback for products that don't have the eprel feature but do have the old energy feature
        labelNested: energyFeatureValue?.imageURL || null
    };
};

const fromProductToEnergyLabel: ModelMapper<
    Product<ProductFieldSet.Full | ProductFieldSet.ProductTile>,
    EnergyLabel | undefined
> = (product) => {
    if (product.eprelRegistrationNumber) {
        return {
            eprelId: product.eprelRegistrationNumber,
            label: null,
            labelNested: null
        };
    } else {
        return fromFeature(product);
    }
};

export const EnergyLabelMapper = {
    fromProductToEnergyLabel: fromProductToEnergyLabel
};
